import { FC, useMemo, useState } from "react";
import Modal from '@atomaro/ui-kit/components/Modal/Modal';
import Button from '@atomaro/ui-kit/components/Button/Button/Button';
import Input from '@atomaro/ui-kit/components/Input/Input';
import { 
  Employee,
  EmployeeAddBody, 
  EmployeeAddEmployee, 
  EmployeeAddFooter, 
  EmployeeAddHead, 
  EmployeeAddInput, 
  EmployeeAddTitle, 
  EmployeeAddWrapper, 
  EmployeeAvatar,
  EmployeeBody,
  EmployeeEmail,
  EmployeeInfoRow,
  EmployeeName
} from "./style";
import { Spacer } from "shared/ui/spacer";
import { formatPhoneNumber, toRem, unmaskPhone } from "shared/helpers";
import { CloseIcon } from "shared/ui/icons";
import { useHookFormMask } from "use-mask-input";
import { useForm } from "react-hook-form";
import { useUserStore } from "entities/user";
import { useDebounceCallback } from "shared/lib/debounce/useDebounce";
import { TUserInfo } from "shared/types";
import profileImg from "shared/assets/img/user.png";
import { format } from "date-fns";
import { useApiariesStore } from "entities/apiaries";
import { toast } from "react-toastify";

type EmployeeAddProps = {
  isOpen: boolean;
  isMobile?: boolean;
  onClose: () => void;
}
type FormValues = {
  phone: string;
}
export const EmployeeAdd: FC<EmployeeAddProps> = ({
  isOpen,
  isMobile,
  onClose
}) => {

  const getUsers = useUserStore(s => s.getUsers);
  const userInfo = useUserStore(s => s.userInfo);
  const addParticipant = useApiariesStore(s => s.addParticipant);
  const getApiaries = useApiariesStore(s => s.getApiaries);

  const [ user, setUser ] = useState<TUserInfo | null>(null);
  const [ isLoading, setIsLoading ] = useState<boolean>(false);
  
  const {
    formState: {errors},
    setError,
    reset,
    handleSubmit,
    getValues,
    register,
    setValue,
    watch,
    clearErrors
  } = useForm<FormValues>({
    defaultValues: {
      phone: ''
    }
  })

  const formValues = watch();

  const isValid = useMemo(() => {
    return /^((8|\+7)[- ]?)?(\(?\d{3}\)?[- ]?)?[\d\- ]{7,10}$/.test(formValues.phone)
  }, [formValues])

  const registerWithMask = useHookFormMask(register)

  const checkUser = useDebounceCallback(async () => {
    const phone = getValues('phone');
    const isValid = /^((8|\+7)[- ]?)?(\(?\d{3}\)?[- ]?)?[\d\- ]{7,10}$/.test(phone)

    if (!isValid) {
      return setError('phone', {message: 'Некорректный номер'})
    }

    clearErrors('phone');

    const users = await getUsers(unmaskPhone(phone));

    if (users[0]) {
      return setUser(users[0])
    }

    setUser(null)
    setError('phone', {message: 'Пользователя с таким номером нет'})
  }, 500)

  const onCloseClick = () => {
    onClose()
    setUser(null);
    reset()
  }

  const avatarSrc = useMemo(() => {
    const avatarId = user?.profile?.avatar?.id;

    return avatarId ? `/ecpp/attachments/files?attachmentId=${avatarId}` : profileImg;
  }, [user])

  const userPhone = useMemo(() => {
    if (user?.credentials.login) {
      return formatPhoneNumber(user.credentials.login)
    }
    return ''
  }, [user])

  const userBirthDate = useMemo(() => {
    if (user?.profile.birthday) {
      return format(user?.profile.birthday, 'dd.MM.yyyy')
    }
    return ''
  }, [user])

  const onSubmit = async () => {
    const id = user?.id;
    if (!id) return;

    setIsLoading(true);

    const isAdded = await addParticipant(id);
    setIsLoading(false);

    if (isAdded) {
      toast.success('Приглашение успешно добавлено');
    } else {
      toast.error('Что-то пошло не так')
    }

    getApiaries(userInfo?.id);
    setUser(null);
    onCloseClick();
  }

  return (
    <Modal 
      isOpen={isOpen}
      centered
      onClickOverlay={!isLoading ? onCloseClick : () => null}
      style={{padding: 0}}
      maxWidth={isMobile ? '100vw' : 'auto'}
      maxHeight={isMobile ? '100dvh' : 'auto'}
    >
      <EmployeeAddWrapper onSubmit={handleSubmit(onSubmit)}>
        <EmployeeAddHead>
          <Spacer 
            width={toRem(24)}
            height={toRem(24)}
          />
          <EmployeeAddTitle>Добавление сотрудника</EmployeeAddTitle>
          <Button 
            iconPrefix={<CloseIcon />}
            variant="ghost"
            size="s"
            onClick={!isLoading ? onCloseClick : () => null}
            disabled={isLoading}
          />
        </EmployeeAddHead>
        <EmployeeAddBody>
          <p>Отправьте приглашение по email или номеру телефона. Сотрудник получит ссылку для регистрации / входа.</p>
          <EmployeeAddInput>
            <Input 
              label="Телефон сотрудника"
              size="l"
              error={errors.phone?.message}
              value={formValues.phone}
              {...registerWithMask("phone", '+7 (999) 999-99-99', {
                required: 'Введите номер',
                inputmode: 'numeric',
                clearMaskOnLostFocus: true,
                onBlur: event => {
                  event.target.blur()
                  event.target.placeholder = '';
                },
                validate: (value) => {
                  if (!value) {
                    return 'Введите номер';
                  }
                  if (!/^((8|\+7)[- ]?)?(\(?\d{3}\)?[- ]?)?[\d\- ]{7,10}$/.test(value)) {
                    return 'Некорректный номер'
                  }
                },
                onChange: event => {
                  let val = event.target.value;
                  if (val.startsWith('+7 (89') || val.startsWith('+7 (7')) {
                    val = val.replace('+7 (89', '+7 (9').replace('+7 (7', '+7 (9')
                  }
                  setValue('phone', val);
                  checkUser();
                },
              })}
            />
          </EmployeeAddInput>
          {Boolean(user) && 
          <EmployeeAddEmployee>
            <p>Найден пользователь:</p>
            <Employee>
              <EmployeeAvatar>
                <img src={avatarSrc} alt="Image" />
              </EmployeeAvatar>
              <EmployeeBody>
                <EmployeeName>{user?.name.first} {user?.name.last}</EmployeeName>
                {Boolean(user?.credentials.email) && <EmployeeEmail>{user?.credentials.email}</EmployeeEmail>}
                <EmployeeInfoRow>
                  <span>тел.</span>
                  <p>{userPhone}</p>
                </EmployeeInfoRow>
                {userBirthDate && 
                <EmployeeInfoRow>
                  <span>дата рождения</span>
                  <p>{userBirthDate}</p>
                </EmployeeInfoRow>
                }
              </EmployeeBody>
            </Employee>
          </EmployeeAddEmployee>
          }

        </EmployeeAddBody>
        <EmployeeAddFooter>
          <Button 
            label="Отменить"
            variant="ghost"
            size="l"
            onClick={!isLoading ? onCloseClick : () => null}
            type="button"
            colorScheme="neutral"
            disabled={isLoading}
          />
          <Button 
            label="Отправить приглашение"
            size="l"
            type="submit"
            disabled={!isValid||!user||isLoading}
          />
        </EmployeeAddFooter>
      </EmployeeAddWrapper>
    </Modal>
  )
}